import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  useStockTransfers,
  useCreateStockTransfer,
  useUpdateStockTransfer,
  useApproveStockTransfer,
  useCompleteStockTransfer,
  useCancelStockTransfer,
} from '@/hooks/useStockTransfers';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useToast } from '@/hooks/useToast';
import { ItemSelector } from '@/components/inventory/ItemSelector';
import type { ItemSelectionResult } from '@/types/purchase-order-form';
import type { StockTransfer, Location } from '@/lib/schemas';
import { cn, formatDate } from '@/lib/utils';
import {
  Plus,
  Package,
  MapPin,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  AlertTriangle,
  User,
  Truck,
} from 'lucide-react';

const statusConfig = {
  requested: { variant: 'warning' as const, label: 'Requested', icon: Clock },
  approved: { variant: 'info' as const, label: 'Approved', icon: CheckCircle },
  picked: { variant: 'default' as const, label: 'Picked', icon: Package },
  shipped: { variant: 'default' as const, label: 'Shipped', icon: Truck },
  received: { variant: 'success' as const, label: 'Received', icon: CheckCircle },
  cancelled: { variant: 'error' as const, label: 'Cancelled', icon: XCircle },
};

const priorityConfig = {
  normal: { variant: 'outline' as const, label: 'Normal' },
  urgent: { variant: 'error' as const, label: 'Urgent' },
};

export default function StockTransfersPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState<StockTransfer | null>(null);
  const [deletingTransfer, setDeletingTransfer] = useState<StockTransfer | null>(null);
  const [selectedTransfer, setSelectedTransfer] = useState<StockTransfer | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'complete' | 'cancel' | null>(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedItem, setSelectedItem] = useState<ItemSelectionResult | null>(null);
  const [isItemSelectorOpen, setIsItemSelectorOpen] = useState(false);

  const { data: locations = [] } = useLocations();
  const locationOptions = locations
    .filter((loc): loc is Location => !!loc.location_id)
    .map((loc) => ({
      value: loc.location_id!,
      label: `${loc.code} - ${loc.name}`,
    }));

  const { data: transfers = [], isLoading, error } = useStockTransfers();
  const createMutation = useCreateStockTransfer();
  const updateMutation = useUpdateStockTransfer();
  const approveMutation = useApproveStockTransfer();
  const completeMutation = useCompleteStockTransfer();
  const cancelMutation = useCancelStockTransfer();

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    ...Object.entries(statusConfig).map(([value, { label }]) => ({ value, label })),
  ];

  const filteredTransfers = useMemo(() => {
    return transfers.filter((transfer) => {
      const matchesSearch =
        transfer.transfer_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        transfer.requested_by.toLowerCase().includes(searchQuery.toLowerCase()) ||
        transfer.item_id?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || transfer.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [transfers, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    transfers.forEach((transfer) => {
      counts.all++;
      counts[transfer.status] = (counts[transfer.status] || 0) + 1;
    });
    return counts;
  }, [transfers]);

  const getLocationName = (locationId: string) => {
    const loc = locations.find(l => l.location_id === locationId);
    return loc ? `${loc.code} - ${loc.name}` : `Location ${locationId}`;
  };

  // Action handlers
  const openActionModal = (transfer: StockTransfer, action: 'approve' | 'complete' | 'cancel') => {
    setSelectedTransfer(transfer);
    setActionType(action);
    setCancelReason('');
    setIsActionModalOpen(true);
  };

  const executeAction = async () => {
    if (!selectedTransfer?.transfer_id) return;

    switch (actionType) {
      case 'approve':
        await approveMutation.mutateAsync({
          id: selectedTransfer.transfer_id,
          approverId: user?.user_id || 'current-user',
        });
        break;
      case 'complete':
        await completeMutation.mutateAsync(selectedTransfer.transfer_id);
        break;
      case 'cancel':
        await cancelMutation.mutateAsync({
          id: selectedTransfer.transfer_id,
          reason: cancelReason,
        });
        break;
    }
    setIsActionModalOpen(false);
    setSelectedTransfer(null);
    setActionType(null);
  };

  const handleCreate = async (data: any) => {
    if (!selectedItem) return;

    await createMutation.mutateAsync({
      from_location_id: data.from_location_id,
      to_location_id: data.to_location_id,
      item_id: selectedItem.item_id,
      quantity: selectedItem.quantity_ordered,
      priority: data.priority || 'normal',
      notes: data.notes,
      status: 'requested',
      requested_by: user?.full_name || 'User',
    });
    setIsCreateOpen(false);
    setSelectedItem(null);
  };

  const handleUpdate = async (data: any) => {
    if (!editingTransfer?.transfer_id) return;

    await updateMutation.mutateAsync({
      id: editingTransfer.transfer_id,
      data: {
        from_location_id: data.from_location_id,
        to_location_id: data.to_location_id,
        quantity: data.quantity,
        priority: data.priority,
        notes: data.notes,
      },
    });
    setEditingTransfer(null);
  };

  const handleDelete = async () => {
    if (deletingTransfer?.transfer_id) {
      await cancelMutation.mutateAsync({
        id: deletingTransfer.transfer_id,
        reason: 'Cancelled by user',
      });
      setDeletingTransfer(null);
    }
  };

  const openEditModal = (transfer: StockTransfer) => {
    setEditingTransfer(transfer);
    setIsCreateOpen(true);
  };

  const getActionButtons = (transfer: StockTransfer) => {
    const buttons: JSX.Element[] = [];

    if (transfer.status === 'requested') {
      buttons.push(
        <Button
          key="approve"
          size="sm"
          variant="primary"
          onClick={() => openActionModal(transfer, 'approve')}
          className="gap-1"
        >
          <CheckCircle className="h-4 w-4" />
          Approve
        </Button>,
        <Button
          key="cancel"
          size="sm"
          variant="destructive"
          onClick={() => openActionModal(transfer, 'cancel')}
          className="gap-1"
        >
          <XCircle className="h-4 w-4" />
          Cancel
        </Button>
      );
    } else if (['approved', 'picked', 'shipped'].includes(transfer.status)) {
      buttons.push(
        <Button
          key="complete"
          size="sm"
          variant="secondary"
          onClick={() => openActionModal(transfer, 'complete')}
          className="gap-1"
        >
          <CheckCircle className="h-4 w-4" />
          Mark Received
        </Button>,
        <Button
          key="cancel"
          size="sm"
          variant="destructive"
          onClick={() => openActionModal(transfer, 'cancel')}
          className="gap-1"
        >
          <XCircle className="h-4 w-4" />
          Cancel
        </Button>
      );
    }

    return buttons;
  };

  const transferFields: FormField[] = editingTransfer
    ? [
        {
          name: 'from_location_id',
          label: 'From Location',
          type: 'select' as const,
          required: true,
          options: locationOptions,
          helperText: 'Source location',
        },
        {
          name: 'to_location_id',
          label: 'To Location',
          type: 'select' as const,
          required: true,
          options: locationOptions,
          helperText: 'Destination location',
        },
        {
          name: 'quantity',
          label: 'Quantity',
          type: 'number' as const,
          required: true,
          placeholder: 'Enter quantity',
        },
        {
          name: 'priority',
          label: 'Priority',
          type: 'select' as const,
          required: true,
          options: [
            { value: 'normal', label: 'Normal' },
            { value: 'urgent', label: 'Urgent' },
          ],
        },
        {
          name: 'notes',
          label: 'Notes',
          type: 'textarea' as const,
          placeholder: 'Additional instructions (optional)',
        },
      ]
    : [
        {
          name: 'from_location_id',
          label: 'From Location',
          type: 'select' as const,
          required: true,
          options: locationOptions,
          helperText: 'Source location',
        },
        {
          name: 'to_location_id',
          label: 'To Location',
          type: 'select' as const,
          required: true,
          options: locationOptions,
          helperText: 'Destination location',
        },
        {
          name: 'priority',
          label: 'Priority',
          type: 'select' as const,
          required: true,
          options: [
            { value: 'normal', label: 'Normal' },
            { value: 'urgent', label: 'Urgent' },
          ],
        },
        {
          name: 'notes',
          label: 'Notes',
          type: 'textarea' as const,
          placeholder: 'Additional instructions (optional)',
        },
      ];

  const actionFields: FormField[] = [
    {
      name: 'reason',
      label: actionType === 'cancel' ? 'Cancellation Reason' : 'Comments',
      type: 'textarea' as const,
      placeholder: actionType === 'cancel' ? 'Enter reason for cancellation' : 'Add comments (optional)',
      required: actionType === 'cancel',
      helperText: actionType === 'cancel' ? 'Please provide a reason for cancellation' : 'Comments are optional',
    },
  ];

  // Custom modal content for create mode
  const renderCreateModalContent = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Select Item</h3>
        {selectedItem ? (
          <div className="p-4 border rounded-lg bg-background">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-medium">{selectedItem.item_name}</p>
                <p className="text-sm text-text-muted">{selectedItem.item_code}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedItem(null)}
              >
                Change
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-text-muted">Quantity:</span>
                <span className="ml-2 font-semibold">{selectedItem.quantity_ordered}</span>
              </div>
              <div>
                <span className="text-text-muted">Available:</span>
                <span className="ml-2">{selectedItem.available_quantity}</span>
              </div>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="w-full justify-center"
            onClick={() => setIsItemSelectorOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Select Item
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Stock Transfers
          </h1>
          <p className="text-text-secondary mt-1">
            Move inventory between locations
          </p>
        </div>
        <Button onClick={() => { setSelectedItem(null); setIsCreateOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          New Transfer
        </Button>
      </div>

      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
        >
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading stock transfers</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: 'All', value: stats.all, status: 'all' as const },
          { label: 'Requested', value: stats.requested || 0, status: 'requested' as const },
          { label: 'Approved', value: stats.approved || 0, status: 'approved' as const },
          { label: 'In Transit', value: (stats.picked || 0) + (stats.shipped || 0), status: 'transit' as const },
          { label: 'Received', value: stats.received || 0, status: 'received' as const },
        ].map(({ label, value, status: statStatus }) => (
          <Card
            key={label}
            className={cn(
              'cursor-pointer transition-all',
              statusFilter === statStatus ? 'ring-2 ring-primary' : ''
            )}
            onClick={() => setStatusFilter(statStatus === 'all' ? 'all' : statusFilter === statStatus ? 'all' : statStatus)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Truck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-text-primary">{value}</p>
                  <p className="text-sm text-text-muted">{label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search by transfer ID, item, or requester..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={10} columns={8} />
      ) : filteredTransfers.length > 0 ? (
        <DataTable
          columns={[
            {
              key: 'transfer_id',
              header: 'Transfer #',
              render: (tr: StockTransfer) => (
                <span className="font-mono text-sm">{tr.transfer_id?.slice(-8).toUpperCase()}</span>
              ),
            },
            {
              key: 'locations',
              header: 'Route',
              render: (tr: StockTransfer) => (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-3 w-3 text-text-muted" />
                  <span>{getLocationName(tr.from_location_id)}</span>
                  <ArrowRight className="h-3 w-3 text-text-muted" />
                  <span>{getLocationName(tr.to_location_id)}</span>
                </div>
              ),
            },
            {
              key: 'item',
              header: 'Item',
              render: (tr: StockTransfer) => (
                <div className="text-sm">
                  <p className="font-medium">{tr.item_id}</p>
                  <p className="text-text-muted">Qty: {tr.quantity}</p>
                </div>
              ),
            },
            {
              key: 'priority',
              header: 'Priority',
              render: (tr: StockTransfer) => {
                const config = priorityConfig[tr.priority];
                return <Badge variant={config.variant}>{config.label}</Badge>;
              },
            },
            {
              key: 'status',
              header: 'Status',
              render: (tr: StockTransfer) => {
                const config = statusConfig[tr.status as keyof typeof statusConfig];
                const Icon = config.icon;
                return (
                  <Badge variant={config.variant}>
                    {Icon && <Icon className="h-3 w-3 mr-1" />}
                    {config.label}
                  </Badge>
                );
              },
            },
            {
              key: 'requested_by',
              header: 'Requested By',
              render: (tr: StockTransfer) => (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-3 w-3 text-text-muted" />
                  <span>{tr.requested_by}</span>
                </div>
              ),
            },
            {
              key: 'requested_at',
              header: 'Date',
              render: (tr: StockTransfer) => (
                <span className="text-sm">{formatDate(tr.requested_at)}</span>
              ),
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (tr: StockTransfer) => (
                <div className="flex items-center gap-2">
                  {getActionButtons(tr)}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditModal(tr)}
                    disabled={tr.status !== 'requested'}
                  >
                    Edit
                  </Button>
                </div>
              ),
            },
          ]}
          data={filteredTransfers}
          loading={false}
          onEdit={openEditModal}
          onDelete={setDeletingTransfer}
          getItemId={(tr) => tr.transfer_id!}
          actionsColumn={true}
        />
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No stock transfers found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first stock transfer to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {filteredTransfers.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No stock transfers found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first stock transfer to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal with custom content */}
      <ModalForm<StockTransfer>
        open={isCreateOpen || !!editingTransfer}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setEditingTransfer(null);
            setSelectedItem(null);
          }
        }}
        title={editingTransfer ? 'Edit Stock Transfer' : 'New Stock Transfer'}
        description="Move items from one location to another"
        fields={transferFields}
        initialData={editingTransfer || undefined}
        onSubmit={editingTransfer ? handleUpdate : handleCreate}
        isLoading={createMutation.isPending || updateMutation.isPending}
      >
        {!editingTransfer && renderCreateModalContent()}
      </ModalForm>

      {/* Confirm Dialogs */}
      <ConfirmDialog
        open={!!deletingTransfer}
        onOpenChange={(open) => !open && setDeletingTransfer(null)}
        title="Cancel Stock Transfer"
        description={`Are you sure you want to cancel transfer ${deletingTransfer?.transfer_id}? This action cannot be undone.`}
        onConfirm={handleDelete}
        isDeleting={cancelMutation.isPending}
      />

      {/* Action Modal (Approve/Complete/Cancel) */}
      <ModalForm
        open={isActionModalOpen}
        onOpenChange={setIsActionModalOpen}
        title={
          actionType === 'approve' ? 'Approve Transfer' :
          actionType === 'complete' ? 'Complete Transfer' :
          'Cancel Transfer'
        }
        description={
          actionType === 'approve' ? `Approve transfer ${selectedTransfer?.transfer_id}` :
          actionType === 'complete' ? `Mark transfer ${selectedTransfer?.transfer_id} as received` :
          `Cancel transfer ${selectedTransfer?.transfer_id}`
        }
        fields={actionFields}
        onSubmit={executeAction}
        isLoading={
          actionType === 'approve' ? approveMutation.isPending :
          actionType === 'complete' ? completeMutation.isPending :
          cancelMutation.isPending
        }
      />

      {/* Item Selector */}
      <ItemSelector
        open={isItemSelectorOpen}
        onOpenChange={setIsItemSelectorOpen}
        onSelect={(item) => {
          setSelectedItem(item);
        }}
        hospitalId={user?.hospital_id || '550e8400-e29b-41d4-a716-446655440000'}
      />
    </div>
  );
}
